import { useCallback, useRef, useState } from "react";
import { addToast, LEAVE_ANIMATION_MS, markLeaving, removeToast, type ToastEntry, type ToastOptions, toastDuration } from "./toastModel";

export interface ToastQueue {
  toasts: ToastEntry[];
  show: (options: ToastOptions) => number;
  dismiss: (id: number) => void;
}

/** The toasts on screen, each closing on its own timer after its exit animation. */
export function useToastQueue(): ToastQueue {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);
  const nextId = useRef(0);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: number) => {
    clearTimeout(timers.current.get(id));
    timers.current.delete(id);
    setToasts((prev) => markLeaving(prev, id));
    setTimeout(() => setToasts((prev) => removeToast(prev, id)), LEAVE_ANIMATION_MS);
  }, []);

  const show = useCallback(
    (options: ToastOptions) => {
      const id = nextId.current++;
      setToasts((prev) => addToast(prev, { id, ...options }));
      timers.current.set(
        id,
        setTimeout(() => dismiss(id), toastDuration(options)),
      );
      return id;
    },
    [dismiss],
  );

  return { toasts, show, dismiss };
}
